import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { GatewayConnectionService } from 'src/app/services/apiHandler/gateway-connection/gateway-connection.service';
import { initDriveMultiDataset, t_DriveMultiDataset } from '../data/drive-multi-dataNames';
import { interval, Subject, takeUntil } from 'rxjs';
import { DidConfigService } from 'src/app/services/apiHandler/did-config/did-config.service';
import { DidDataRetrievalService, t_DataIdentifier, t_DataPoint } from 'src/app/services/apiHandler/did-data-retrieval/did-data-retrieval.service';
import { dataIdentifierEqual } from 'src/app/services/helper/did-handling-helper';

@Component({
  selector: 'app-drive-multi',
  templateUrl: './drive-multi.component.html',
  styleUrls: ['./drive-multi.component.scss']
})
export class DriveMultiComponent implements OnInit, OnDestroy {

  public driveMultiDataset: t_DriveMultiDataset = JSON.parse(JSON.stringify(initDriveMultiDataset));
  public queryFrequency: number = 1000;
  public loadedAndQueryStarting: boolean = false;
  public imgLoaded: boolean = false;
  public pollingFrequency: number;
  private destroy$: Subject<void> = new Subject<void>();
  private destroyed: boolean = false;

  gearActive: boolean = false;
  currentGear: string = 'N';
  breakActive: boolean = false;

  constructor(public gatewayConnectionService: GatewayConnectionService, private didConfigService: DidConfigService, private dataRetrieval: DidDataRetrievalService) {
    if (this.queryFrequency <= 65) {
      this.pollingFrequency = 50;
    } else {
      this.pollingFrequency = parseFloat((this.queryFrequency * 0,75).toFixed(0));
    }
  }
  
  ngOnInit(): void {
    this.setupServices().then(() => {
      if(!this.destroyed) {
        this.dataRetrieval.initService();
        this.getDataIdentifiersToDataNamesFromConfig();
        this.postDataIdentifierToQueryToBackend().then(() => {
          if(this.destroyed) {
            this.ngOnDestroy();
          }
          this.dataRetrieval.startDataQuery();
          this.loadedAndQueryStarting = true;
          this.startPeriodicPolling();
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    this.dataRetrieval.stopSendQueryAliveState();
    this.dataRetrieval.stopDataQuery();
    this.dataRetrieval.cleanDataPoints();
    this.gatewayConnectionService.checkGatewayAlive().subscribe({
      next: (isAlive) => {
        if(isAlive){
          this.driveMultiDataset.dataPoints.forEach(data => {
            this.didConfigService.removeDidFromQueryList({ecu_id: data.dataName.identifier!.ecu_id, data_id: data.dataName.identifier!.data_id}).subscribe();
          });
        }
      }
    });
  }

  private setupServices(): Promise<void> {
    return new Promise((resolve) => {
      this.gatewayConnectionService.establishConnectionToGateway().then(
        () => {
          resolve();
        },
        () => {
          console.error('ERROR');
        }
      );
    });
  }

  public getDataIdentifiersToDataNamesFromConfig() {
    this.driveMultiDataset.dataPoints.forEach((driveDataPoint) => {
      driveDataPoint.dataName.identifier = this.didConfigService.getDataIdentifierOfDataName(driveDataPoint.dataName.name);
      driveDataPoint.dataName.didInformation = this.didConfigService.getDidInformation(driveDataPoint.dataName.identifier);
    });
    this.driveMultiDataset.dataPoints = this.driveMultiDataset.dataPoints.map((dataPoint) => dataPoint).filter((dataPoint) => dataPoint.dataName.identifier?.ecu_id !== undefined);
    if(this.destroyed) {
      this.ngOnDestroy();
    }
  }

  private postDataIdentifierToQueryToBackend(): Promise<void> {
    return new Promise((resolve) => {
      let dataIdentifierList: t_DataIdentifier[] = this.driveMultiDataset.dataPoints.map((dataPoint) => dataPoint.dataName.identifier).filter((identifier) => identifier?.ecu_id !== undefined) as t_DataIdentifier[];
      this.didConfigService.postNewDidQueryList(dataIdentifierList).subscribe({
        complete: () => {
          resolve();
        },
      });
    });
  }
  
  private startPeriodicPolling() {
    interval(this.pollingFrequency)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.getDataFromBackend();
      });
  }

  private getDataFromBackend() {
    this.dataRetrieval.getAllDidsValues().subscribe((response) => {
      if(response.body != null) {
        this.processRawData(response.body);
      }
    });
  }

  private processRawData(rawData: t_DataPoint[]) {
    rawData.forEach((dataPoint) => {
      if (!dataPoint.data || new Date(dataPoint.data.timestamp).getTime() < 0) {
        return;
      }
      let indexInDataset: number = this.findIndexForIdentifierDataset(dataPoint.identifier);
      let normalizedValue: number = this.normalizedValue(
        dataPoint.data.value[0],
        this.driveMultiDataset.dataPoints[indexInDataset].dataName.didInformation!.conversionFactor,
        this.driveMultiDataset.dataPoints[indexInDataset].dataName.didInformation!.conversionOffset
      );
      this.driveMultiDataset.dataPoints[indexInDataset].value = normalizedValue;
      this.driveMultiDataset.dataPoints[indexInDataset].unit = this.driveMultiDataset.dataPoints[indexInDataset].dataName.didInformation!.unit;
      // GEAR: HydrostatDirectionGearSelectionActual
      if(indexInDataset == 8) {
        this.handleGear(normalizedValue);
      }
      /*if(this.driveMultiDataset.dataPoints[indexInDataset].dataName.name == 'HydrostatDirectionGearSelectionActual') {
        this.handleGear(normalizedValue);
      }*/
      // BREAK: ParkingBrakeClosed
      if(indexInDataset == 9) {
        this.handleBreak(normalizedValue);
      }
      /*if(this.driveMultiDataset.dataPoints[indexInDataset].dataName.name == 'ParkingBrakeClosed') {
        this.handleBreak(normalizedValue);
      }*/
    });
  }

  private findIndexForIdentifierDataset(searchIdentifier: t_DataIdentifier): number {
      let index: number = this.driveMultiDataset.dataPoints.findIndex((datapoint) => {
        if (datapoint.dataName.identifier) {
          return dataIdentifierEqual(datapoint.dataName.identifier, searchIdentifier);
        } else {
          return false;
        }
      });
      return index;
    }
  
  private normalizedValue(value: number, factor: number, offset: number) {
    if(factor == 0) {
      factor = 1;
    }
    let res: number = (value * factor) + offset;
    res = parseFloat(res.toFixed(1));
    return res;
  }

  private handleGear(value: any) {
    if(value == 0) {
      this.currentGear = 'N'; //neutral
    } else if(value == 1) {
      this.currentGear = 'F'; //forward
    } else if(value == -1) {
      this.currentGear = 'R'; // reverse
    }
  }

  private handleBreak(value: any) {
    if(value == 0) {
      this.breakActive = false;
    } else if(value == 1) {
      this.breakActive = true;
    }
  }

}
